import { Component, Inject, LOCALE_ID, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import {
    CompoundOrigin,
    CompoundTranslationType,
    LocationInfo, PolygonType,
    PreliminaryScreeningLocationDetailView, PreliminaryScreeningLocationResultItem, ReassessmentType,
    RemovalReasonTexts, RiskAssessmentStatus,
    ScreeningFlag,
    ScreeningLocationType,
    ScreeningPolygonResult,
    ScreeningPolygonStatus
} from 'src/app/contracts';
import { LocationService, PreliminaryScreeningService } from 'src/app/services';
import { BaseComponent } from 'src/app/shared/baseComponent';
import { LocationDetailsHelper } from '../location-details/location-details.helper';

@Component({
    selector: 'app-preliminary-screening-location-details',
    templateUrl: './preliminary-screening-location-details.component.html',
    styleUrls: ['./preliminary-screening-location-details.component.scss'],
    providers: [LocationDetailsHelper]
})
export class PreliminaryScreeningLocationDetailsComponent extends BaseComponent implements OnInit {
    @ViewChild('reasessModalCloseButton') public reasessModalCloseButton: any;

    constructor(
        @Inject(LOCALE_ID) locale,
        private readonly route: ActivatedRoute,
        private readonly router: Router,
        private readonly locationService: LocationService,
        private readonly preliminaryScreeningService: PreliminaryScreeningService) {
        super(locale);
    }

    // Helpers to get access to enums in the template html
    public riskAssessmentStatus = RiskAssessmentStatus; // Make enum accessible from the html-template
    public reassessmentType = ReassessmentType; // Make enum accessible from the html-template
    public screeningLocationType = ScreeningLocationType;
    public screeningPolygonStatus = ScreeningPolygonStatus;
    public polygonType = PolygonType;

    public location: LocationInfo = new LocationInfo({});
    public preliminaryScreeningLocationDetails = new PreliminaryScreeningLocationDetailView({});
    public reassessRemark: string;
    public missingRemark = false;
    public reassessmentPolygon: PolygonType;
    public reassessToStatus: ScreeningPolygonStatus;
    public flags: ScreeningFlag = 0;
    public screeningFlag = ScreeningFlag;
    public loading = false;
    public showV1InsteadOfV2 = false;
    public compoundTranslationType = CompoundTranslationType;
    public get v2ScreeningResults(): { [key: number]: Array<PreliminaryScreeningLocationResultItem> } {
        if (!this.location) {
            return {};
        }
        return this.groupScreeningResultByPolygonArea(this.preliminaryScreeningLocationDetails.v2PreliminaryScreenings);
    }

    public get showV2LocationDetails(): boolean {

        return this.preliminaryScreeningLocationDetails.hasV2Polygon && this.preliminaryScreeningLocationDetails.v2PolygonResult.hasData
            && !this.showV1InsteadOfV2;
    }

    public get v1ScreeningResults(): { [key: number]: Array<PreliminaryScreeningLocationResultItem> } {
        if (!this.location) {
            return {};
        }
        return this.groupScreeningResultByPolygonArea(this.preliminaryScreeningLocationDetails.v1PreliminaryScreenings);
    }

    public get showV1LocationDetails(): boolean {
        // return this.location.hasV1Polygon && !this.location.hasV2Polygon;
        return this.preliminaryScreeningLocationDetails.hasV1Polygon && this.preliminaryScreeningLocationDetails.v1PolygonResult.hasData
            && !this.showV2LocationDetails;
    }

    public ngOnInit(): void {
        this.loadData();
    }

    public getRemovalReason(removalEnum): string {
        const reason = RemovalReasonTexts.get(removalEnum);
        return reason ? reason : '';
    }

    private loadData(): void {
        this.loading = true;
        const id = this.route.snapshot.paramMap.get('id');

        this.preliminaryScreeningService.getLocationDetails(id).subscribe(
            res => {
                this.updateScreeningLocationDetails(res);
                this.loading = false;
            },
            () => {
                this.loading = false;
            }
        );
    }

    private updateScreeningLocationDetails(screeningLocationDetails: PreliminaryScreeningLocationDetailView): void {
        if (!screeningLocationDetails) {
            return;
        }
        screeningLocationDetails.v1PreliminaryScreenings.forEach(p => {
            if (p.factor.toString() === 'Infinity' || p.factor.toString() === 'NaN') {
                p.factor = 0;
            }
        });
        screeningLocationDetails.v1PreliminaryScreenings = screeningLocationDetails.v1PreliminaryScreenings.sort((a, b) => {
            if (a.factor < b.factor) {
                return 1;
            }
            if (a.factor > b.factor) {
                return -1;
            }
            return 0;
        });

        screeningLocationDetails.v2PreliminaryScreenings.forEach(p => {
            if (p.factor.toString() === 'Infinity' || p.factor.toString() === 'NaN') {
                p.factor = 0;
            }
        });
        screeningLocationDetails.v2PreliminaryScreenings = screeningLocationDetails.v2PreliminaryScreenings.sort((a, b) => {
            if (a.factor < b.factor) {
                return 1;
            }
            if (a.factor > b.factor) {
                return -1;
            }
            return 0;
        });

        if (screeningLocationDetails.hasV1Polygon && screeningLocationDetails.hasV2Polygon) {
            // If V2 is less than 1, V1 is higher than 1 then it is V1 factor that is shown
            // If V2 is less than 1, V1 is less than 1, then it is V2 factor that is shown
            // V2 is higher than 1, then it is always V2 factor that has to be shown
            const v2Screenings = screeningLocationDetails.v2PreliminaryScreenings.filter(p => !p.removed);
            const maxV2Factor = Math.max.apply(Math, v2Screenings.map(p => p.factor));
            const v1Screenings = screeningLocationDetails.v1PreliminaryScreenings.filter(p => !p.removed);
            const maxV1Factor = Math.max.apply(Math, v1Screenings.map(p => p.factor));

            if (maxV2Factor < 1 && maxV1Factor > 1) {
                this.showV1InsteadOfV2 = true;
            }
        }

        this.preliminaryScreeningLocationDetails = screeningLocationDetails;
        this.location = screeningLocationDetails.location;
        this.calculateFlags();
    }

    public getLeftBoxTitle(): string {
        if (!this.location) {
            return '-';
        }

        return 'Foreløbig screening';
    }

    // Yeah, its a hack
    public isNaN(value: any): boolean {
        const res = isNaN(value) || !isFinite(value);
        return res;
    }

    private calculateFlags(): void {
        // Check elements
        this.preliminaryScreeningLocationDetails.v1PreliminaryScreenings
            .concat(this.preliminaryScreeningLocationDetails.v2PreliminaryScreenings)
            .forEach(element => {
                // tslint:disable-next-line: no-bitwise
                this.flags = this.flags | element.flag;
            });
    }

    public reassessmentEllipsisComment(polygonResult: ScreeningPolygonResult): string {
        if (polygonResult.reassessment != null) {
            if (polygonResult.reassessment.comment.length > 50) {
                return polygonResult.reassessment.comment.substring(0, 48) + ' ...';
            }

            return polygonResult.reassessment.comment;
        }

        return '';
    }

    public hasFlag(flag: ScreeningFlag): boolean {
        // tslint:disable-next-line: no-bitwise
        return ((this.flags & flag) === flag);
    }

    public hasAnyFlags(): boolean {
        return this.flags !== 0;
    }

    public reassess(): void {
        // Check requirements
        if (this.loading) {
            return;
        }

        if (!this.reassessRemark) {
            this.missingRemark = true;
            return;
        }

        this.loading = true;

        this.locationService.reassessPolygon(
            this.location.id,
            this.reassessmentPolygon,
            this.reassessToStatus === ScreeningPolygonStatus.NoRisk
                ? RiskAssessmentStatus.Risk : RiskAssessmentStatus.NoRisk,
            this.reassessRemark
        ).subscribe(() => {
            this.reasessModalCloseButton.nativeElement.click();
            this.loading = false;

            this.loadData();
        });

    }

    public getObjectKeys(obj: any): Array<string> {
        if (!obj) {
            return [];
        }
        return Object.keys(obj);
    }

    private groupScreeningResultByPolygonArea(
        screeningResults: Array<PreliminaryScreeningLocationResultItem>): { [key: number]: Array<PreliminaryScreeningLocationResultItem> } {
        const groups = screeningResults.reduce((item, value) => {
            item[value.polygonArea] = item[value.polygonArea] || [];
            item[value.polygonArea].push(value);
            return item;
        }, {} as { [key: number]: Array<PreliminaryScreeningLocationResultItem> });

        return groups;
    }

    public getCompoundOriginText(origin: number): string{
        switch (origin){
            case CompoundOrigin.JAR:
                return 'JAR';
            case CompoundOrigin.Landfill:
                return 'Losseplads';
            default:
                return '-';
        }
    }

    public filterScreeningResults(
        screeningResults: Array<PreliminaryScreeningLocationResultItem>,
        compoundTranslationType: CompoundTranslationType): Array<PreliminaryScreeningLocationResultItem> {
        return screeningResults.filter(p => p.compoundTranslationType === compoundTranslationType);
    }
}
